import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Button, ButtonBase, Dialog, IconButton, Tooltip, Typography } from '@mui/material'
import { QRCodeSVG } from 'qrcode.react'
import React from 'react'
import toast from 'react-hot-toast'

const getClientUrl = () => window.location.origin

/** First word of the pointed Hebrew title (SBL Hebrew), as in the book. */
const MANIFESTO_TITLE_SBL = 'הַֽמָּֽנִיפֶ֙סְט֙'

export interface InvitationShareDialogProps {
  open: boolean
  onClose: () => void
  /** Shown in the browser tooltip on the title (invite recipient). */
  recipientName: string
  token: string
}

export const InvitationShareDialog: React.FC<InvitationShareDialogProps> = ({
  open,
  onClose,
  recipientName,
  token,
}) => {
  const inviteUrl = `${getClientUrl()}/invite/${token}`

  const copyLink = () => {
    void navigator.clipboard.writeText(inviteUrl)
    toast.success('Link copied')
  }

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      scroll="paper"
      BackdropProps={{
        sx: {
          bgcolor: 'var(--bg-surface)',
          opacity: '1 !important',
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          m: 0,
          maxHeight: '100%',
          bgcolor: 'var(--bg-surface)',
          backgroundImage: `
            radial-gradient(ellipse 100% 60% at 50% -10%, rgba(99, 102, 241, 0.14), transparent 52%),
            radial-gradient(ellipse 70% 45% at 100% 100%, rgba(16, 185, 129, 0.08), transparent 50%),
            radial-gradient(ellipse 55% 40% at 0% 90%, rgba(59, 130, 246, 0.07), transparent 45%)
          `,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          minHeight: 48,
          px: 1,
          pt: 'max(8px, env(safe-area-inset-top))',
          pr: 'max(8px, env(safe-area-inset-right))',
        }}
      >
        <Tooltip title="Close">
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              color: 'var(--text-secondary)',
              '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--text-primary)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: { xs: 2.5, sm: 3 },
          pt: { xs: 1, sm: 1.5 },
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          maxWidth: 480,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            width: '100%',
            maxWidth: 480,
            mb: 2,
          }}
        >
          <Typography
            component="h1"
            dir="rtl"
            lang="he"
            title={recipientName}
            sx={{
              fontFamily: "'SBL Hebrew', 'Times New Roman', serif",
              fontSize: { xs: '2.35rem', sm: '2.75rem', md: '3rem' },
              fontWeight: 400,
              lineHeight: 1.8,
              color: 'var(--text-primary)',
              letterSpacing: '0.03em',
              fontFeatureSettings: '"ccmp", "mark", "mkmk"',
              textAlign: 'center',
              px: 0.5,
            }}
          >
            {MANIFESTO_TITLE_SBL}
          </Typography>

          <Box
            sx={{
              p: { xs: 1.5, sm: 1.75 },
              borderRadius: 3,
              bgcolor: '#ffffff',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.08) inset',
            }}
          >
            <QRCodeSVG value={inviteUrl} size={208} level="M" />
          </Box>
        </Box>

        <ButtonBase
          component="div"
          onClick={copyLink}
          aria-label="Copy invitation link to clipboard"
          sx={{
            alignSelf: 'stretch',
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            p: 1.5,
            borderRadius: 2,
            border: '1px solid var(--border-subtle)',
            bgcolor: 'rgba(255, 255, 255, 0.04)',
            backdropFilter: 'blur(8px)',
            textAlign: 'left',
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'var(--bg-hover)',
              borderColor: 'var(--text-tertiary)',
            },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'var(--text-tertiary)',
              outlineOffset: 2,
            },
          }}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              wordBreak: 'break-all',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: '0.72rem',
              lineHeight: 1.55,
              color: 'var(--text-secondary)',
              pointerEvents: 'none',
            }}
          >
            {inviteUrl}
          </Typography>
          <ContentCopyIcon
            sx={{
              flexShrink: 0,
              fontSize: 20,
              color: 'var(--icon-default)',
              mt: 0.15,
              pointerEvents: 'none',
            }}
            aria-hidden
          />
        </ButtonBase>
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2.5, sm: 3 },
          pt: 0.5,
          pb: 'max(16px, env(safe-area-inset-bottom))',
          width: '100%',
          maxWidth: 480,
          mx: 'auto',
        }}
      >
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={onClose}
          sx={{
            py: 1.35,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            borderRadius: 2,
            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.35)',
          }}
        >
          Done
        </Button>
      </Box>
    </Dialog>
  )
}
